import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  api,
  type CasoDetalle,
  type EvidenciaOut,
  type PaqueteOut,
} from "@/lib/api";
import Modal from "@/ui/Modal";
import AyudaVista from "@/ui/AyudaVista";
import { useToast } from "@/ui/Toast";
import { ToggleTrack } from "@/ui/Toggle";
import { ayudaGenerarPaquete } from "@/components/ayuda/vistas/generarPaquete";
import { fmtFechaCargaLarga } from "@/lib/fechas";
import { fmtBytes } from "@/lib/formato";
import { copiarTexto } from "@/lib/copiar";

type Props = {
  caso: CasoDetalle;
  evidencias: EvidenciaOut[];
  onClose: () => void;
  onCreated?: (p: PaqueteOut) => void;
};

const TAG_MENSAJE_CLIENTE = "mensaje-cliente-img";
// Fotos de constancia subidas desde la bitácora: son material INTERNO
// (la captura de un WhatsApp, un mensaje del cliente). Se pueden marcar a
// mano, pero no entran solas al ZIP — mandarle eso al proveedor sería un
// gol en contra.
const TAG_BITACORA = "bitacora";
// Archivos que el PROVEEDOR nos mandó en su ticket (manuales, logs). No entran
// solos al ZIP: devolverle a Jimi el PDF que Jimi mandó no tiene sentido. Se
// pueden marcar a mano si de verdad se quieren reenviar.
// ⚠️ Espejo de TAG_PROVEEDOR en `domain/adjuntos_proveedor.py`.
const TAG_PROVEEDOR = "proveedor-doc";

/** ¿Se queda FUERA de la preselección? (material interno, nunca al proveedor)
 *  Fuente única: la usan la selección inicial Y el efecto del IMEI. Tenerlo
 *  en dos lados fue justo el bug del 2026-08-11: el efecto pisaba la
 *  exclusión inicial y en el 78% de los casos (los de 1 solo IMEI, que
 *  arrancan con ese IMEI elegido) las fotos de bitácora volvían a quedar
 *  marcadas. El usuario siempre las puede marcar a mano. */
function esInterna(e: EvidenciaOut): boolean {
  return e.tags.includes(TAG_BITACORA) || e.tags.includes(TAG_PROVEEDOR);
}
/** Sentinela en el select para "Todos los IMEIs (multi)" — password del TE. */
const TODOS_IMEIS = "todos";

export default function GenerarPaqueteModal({ caso, evidencias, onClose, onCreated }: Props) {
  const toast = useToast();

  // Default: 1 IMEI → ese (nombre con IMEI); 0 o N IMEIs → "General"
  // (nombre con TE). El password siempre sale del TE; el IMEI solo afecta
  // como se llama el ZIP para que el proveedor lo identifique.
  const [imeiSel, setImeiSel] = useState<number | typeof TODOS_IMEIS>(
    caso.imeis.length === 1 ? caso.imeis[0].id : TODOS_IMEIS,
  );

  // Pre-seleccion: si hay evidencias con tag mensaje-cliente-img, esas
  // entran por default. Si no hay, todas las del caso (patron viejo).
  const [seleccion, setSeleccion] = useState<Set<number>>(() => {
    const conTagCliente = evidencias.filter((e) =>
      e.tags.includes(TAG_MENSAJE_CLIENTE),
    );
    if (conTagCliente.length > 0) {
      return new Set(conTagCliente.map((e) => e.id));
    }
    return new Set(evidencias.filter((e) => !esInterna(e)).map((e) => e.id));
  });
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState<PaqueteOut | null>(null);
  const [sinPassword, setSinPassword] = useState(false);

  const tieneTagCliente = useMemo(
    () => evidencias.some((e) => e.tags.includes(TAG_MENSAJE_CLIENTE)),
    [evidencias],
  );

  // Cuando el usuario cambia a un IMEI especifico, sugerir las evidencias
  // de ese IMEI + las sin asignar (incluye mensaje-cliente-img), MENOS las
  // internas. Ojo: este efecto corre también al MONTAR cuando el caso tiene un
  // solo IMEI (87 de 111 casos), así que es el camino normal, no el raro.
  useEffect(() => {
    if (imeiSel === TODOS_IMEIS) return;
    const filtradas = evidencias.filter(
      (e) => (e.imei_id === imeiSel || e.imei_id === null) && !esInterna(e),
    );
    setSeleccion(new Set(filtradas.map((e) => e.id)));
  }, [imeiSel, evidencias]);

  const imeiObj = caso.imeis.find((i) => i.id === imeiSel) ?? null;

  // Password = SIEMPRE ultimos 6 del TE, sin importar IMEI seleccionado.
  // El IMEI solo afecta el nombre del ZIP. El TE es unico por caso y el
  // cliente lo ve en el asunto del correo.
  const passwordPreview = useMemo(() => {
    const te = caso.ticket_externo || "";
    const digitos = te.replace(/\D/g, "");
    return digitos.length >= 6 ? digitos.slice(-6) : digitos.padStart(6, "0") || "—";
  }, [caso.ticket_externo]);

  const passwordOrigen = useMemo(
    () => `ultimos 6 del TE ${caso.ticket_externo ?? "—"}`,
    [caso.ticket_externo],
  );

  const nombrePreview = useMemo(() => {
    if (imeiObj) {
      const modelo = (imeiObj.modelo_real || caso.modelo || "EQUIPO").replace(/\s+/g, "_");
      return `${modelo}_${imeiObj.imei}.zip`;
    }
    const modelo = (caso.modelo || "EQUIPO").replace(/\s+/g, "_");
    const ident = (caso.ticket_externo || caso.folio || "VARIOS")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();
    return `${modelo}_VARIOS_${ident}.zip`;
  }, [imeiObj, caso.modelo, caso.ticket_externo, caso.folio]);

  function toggle(id: number) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function preselectMensajeCliente() {
    const ids = evidencias
      .filter((e) => e.tags.includes(TAG_MENSAJE_CLIENTE))
      .map((e) => e.id);
    setSeleccion(new Set(ids));
  }

  async function generar() {
    if (seleccion.size === 0) {
      toast.error("Selecciona al menos una evidencia");
      return;
    }
    setSubmitting(true);
    try {
      const payload: {
        imei_id: number | null;
        evidencia_ids: number[];
        cifrar: boolean;
      } = {
        imei_id: imeiSel === TODOS_IMEIS ? null : imeiSel,
        evidencia_ids: Array.from(seleccion),
        cifrar: !sinPassword,
      };
      const p = await api.post<PaqueteOut>(`/api/casos/${caso.id}/paquetes`, payload);
      setResultado(p);
      toast.success("Paquete generado", p.nombre_zip);
      onCreated?.(p);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo generar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  // La misma Ayuda en el formulario y en el resultado: es una sola ventana.
  const ayuda = (
    <AyudaVista titulo="Generar paquete ZIP" {...ayudaGenerarPaquete({ tieneTagCliente })} />
  );

  async function copiar(text: string, label: string) {
    if (await copiarTexto(text)) toast.success(`${label} copiado`);
    else toast.error("No se pudo copiar");
  }

  // Resultado tras generar
  if (resultado) {
    return (
      <Modal
        open={true}
        onOpenChange={(v) => !v && onClose()}
        title="Paquete generado"
        description="Comparte el enlace de descarga y la contraseña con el destinatario del correo."
        size="lg"
        ayuda={ayuda}
        widthClass="sm:w-[92vw] sm:max-w-3xl"
        footer={<button onClick={onClose} className="btn-primary">Listo</button>}
      >
        <div className="space-y-3">
          <div className="text-sm">
            <strong className="text-accent-400 font-mono">{resultado.nombre_zip}</strong>
            <span className="text-zinc-500 ml-2">
              {fmtBytes(resultado.size_bytes)}
            </span>
          </div>

          <div>
            <Label>Enlace de descarga (público, 90 días)</Label>
            <div className="flex gap-2 mt-1">
              <input
                readOnly
                value={resultado.url_publica}
                className="input flex-1 font-mono text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button onClick={() => copiar(resultado.url_publica, "Enlace")} className="btn-primary text-xs">
                Copiar
              </button>
            </div>
          </div>

          {resultado.password ? (
          <div>
            <Label>Contraseña (últimos 6 del TE)</Label>
            <div className="flex gap-2 mt-1 items-center">
              <code className="bg-ink-800 border border-ink-600 rounded px-3 py-2 font-mono text-base flex-1">
                {resultado.password}
              </code>
              <button onClick={() => copiar(resultado.password, "Contraseña")} className="btn-primary text-xs">
                Copiar
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Si el cliente pregunta, dile que es <strong>los últimos 6 dígitos del TE</strong>
              {caso.ticket_externo ? ` (${caso.ticket_externo})` : ""} — lo ve en el asunto
              del correo.
            </p>
          </div>
          ) : (
          <div className="text-xs text-zinc-400 bg-ink-800 border border-ink-700 rounded p-3">
            ZIP <strong>sin contraseña</strong> — el proveedor solo abre el enlace y descarga.
          </div>
          )}

          <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded p-3 text-xs">
            <p className="font-medium mb-1">⚠ Importante:</p>
            <ul className="list-disc list-inside space-y-0.5">
              {resultado.password ? (
                <>
                  <li>El ZIP está cifrado de forma segura.</li>
                  <li>La contraseña va en el mismo correo (es el patrón acordado).</li>
                </>
              ) : (
                <li>El ZIP no tiene contraseña (más simple para el proveedor).</li>
              )}
              <li>Cada descarga queda registrada (con dirección IP y fecha/hora).</li>
              <li>
                Después de{" "}
                {Math.floor(
                  (new Date(resultado.expira_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
                )}{" "}
                días el enlace expira.
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    );
  }

  // Formulario
  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title="Generar paquete ZIP"
      description="Empaqueta evidencias en un ZIP cifrado para enviar por correo."
      size="lg"
      ayuda={ayuda}
      widthClass="sm:w-[92vw] sm:max-w-3xl"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button
            onClick={generar}
            disabled={submitting || seleccion.size === 0}
            className="btn-primary disabled:opacity-50"
          >
            {submitting
              ? "Generando..."
              : `Generar (${seleccion.size} archivo${seleccion.size === 1 ? "" : "s"})`}
          </button>
        </>
      }
    >
      {/* Columna flex, no `space-y`: la lista de archivos necesita un padre
          flexible para poder crecer con el modal. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div>
          <Label>Asignar paquete a IMEI (opcional · solo afecta el nombre del ZIP)</Label>
          <select
            className="input mt-1"
            value={imeiSel}
            onChange={(e) =>
              setImeiSel(e.target.value === TODOS_IMEIS ? TODOS_IMEIS : parseInt(e.target.value))
            }
          >
            <option value={TODOS_IMEIS}>
              — General del caso (sin IMEI específico) —
            </option>
            {caso.imeis.map((i) => (
              <option key={i.id} value={i.id}>
                IMEI {i.imei}
                {i.modelo_real ? ` · ${i.modelo_real}` : ""}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              className="sr-only"
              checked={sinPassword}
              onChange={(e) => setSinPassword(e.target.checked)}
            />
            <ToggleTrack checked={sinPassword} />
            <span className="text-xs text-zinc-300">
              Sin contraseña{" "}
              <span className="text-zinc-500">(más simple para el proveedor)</span>
            </span>
          </label>
          <p className="text-xs text-zinc-500 mt-1">
            {sinPassword ? (
              <span className="text-zinc-500">ZIP sin contraseña · </span>
            ) : (
              <>
                Contraseña:{" "}
                <code className="font-mono text-accent-400">{passwordPreview}</code>{" "}
                <span className="text-zinc-500">({passwordOrigen})</span>
                {" · "}
              </>
            )}
            ZIP: <code className="font-mono text-accent-400">{nombrePreview}</code>
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label>
              Archivos a incluir ({seleccion.size}/{evidencias.length}){" "}
              <span className="normal-case tracking-normal text-zinc-500">
                · más recientes arriba
              </span>
            </Label>
            <div className="flex gap-2 text-xs">
              {tieneTagCliente && (
                <button
                  type="button"
                  onClick={preselectMensajeCliente}
                  className="text-accent-400 hover:underline"
                >
                  Solo del Mensaje al cliente
                </button>
              )}
              <button
                type="button"
                onClick={() => setSeleccion(new Set(evidencias.map((e) => e.id)))}
                className="text-accent-400 hover:underline"
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setSeleccion(new Set())}
                className="text-zinc-400 hover:text-white"
              >
                Ninguno
              </button>
            </div>
          </div>
          <ul className="scroll-sutil mt-1 max-h-64 divide-y divide-ink-700 overflow-y-auto rounded border
                         border-ink-700 sm:max-h-80 lg:max-h-none lg:min-h-[12rem] lg:flex-1">
            {evidencias.map((e) => {
              const checked = seleccion.has(e.id);
              const sz = fmtBytes(e.size_bytes);
              const esMensajeCliente = e.tags.includes(TAG_MENSAJE_CLIENTE);
              const esBitacora = e.tags.includes(TAG_BITACORA);
              const esDelProveedor = e.tags.includes(TAG_PROVEEDOR);
              return (
                <li key={e.id}>
                  <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-ink-800 cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() => toggle(e.id)}
                    />
                    <ToggleTrack checked={checked} />
                    {/* Mobile: el nombre se queda con su renglón completo
                        (`basis-full`) y la meta baja debajo; en sm+ todo vuelve
                        a una sola línea. Un solo markup para los tres anchos.
                        Las fichas se explican en la Ayuda (eran tooltips); el
                        IMEI y la hora de carga, que solo vivían en el tooltip,
                        ahora se ven. */}
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="min-w-0 basis-full truncate text-sm sm:basis-auto sm:flex-1">
                        {e.nombre_original}
                      </span>
                      {esMensajeCliente && (
                        <span className="shrink-0 text-[10px] uppercase text-accent-300 bg-accent-500/10 border border-accent-500/30 px-1.5 py-0.5 rounded">
                          📎 mensaje
                        </span>
                      )}
                      {esBitacora && (
                        <span className="shrink-0 text-[10px] uppercase text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                          📓 bitácora
                        </span>
                      )}
                      {esDelProveedor && (
                        <span className="shrink-0 text-[10px] uppercase text-sky-300 bg-sky-500/10 border border-sky-500/30 px-1.5 py-0.5 rounded">
                          📥 del proveedor
                        </span>
                      )}
                      {e.imei_id && (
                        <span className="shrink-0 text-[10px] tabular-nums text-accent-400">
                          IMEI {caso.imeis.find((i) => i.id === e.imei_id)?.imei ?? ""}
                        </span>
                      )}
                      <span className="shrink-0 text-[11px] tabular-nums text-zinc-500">
                        {fmtFechaCargaLarga(e.created_at)}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-zinc-500 sm:w-16 sm:text-right">
                        {sz}
                      </span>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Modal>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="text-xs uppercase tracking-wide text-zinc-400">{children}</span>;
}
